import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useMutation, useQuery } from '@vue/apollo-composable';
import { AddFavoriteDocument, ListFavoritesDocument, RemoveFavoriteDocument } from '@teamango/graphql';
import type { FavoriteFieldsFragment } from '@teamango/graphql';

import { useAuth } from '@/shared/composables/useAuth';
import { resolveGraphqlErrorDescription } from '@/shared/tools/graphql-error';
import { savedViewSlug } from '@/modules/saved-views/tools/saved-view';

/** A favorite whose view resolved — the only kind the sidebar can link. */
export type ResolvedFavorite = FavoriteFieldsFragment & {
  savedView: NonNullable<FavoriteFieldsFragment['savedView']> & { scopeKey: string };
};

/** The view route each surface opens a saved view under, and its scope param. */
const VIEW_ROUTE: Record<string, { name: string; scopeParam: string; prefix: string }> = {
  'team-issues': { name: 'TeamIssuesView', scopeParam: 'teamKey', prefix: 'team' },
};

/**
 * Where a favorite opens: the SAME route the view's own switcher entry builds
 * (`/team/:teamKey/views/:slug`). That sameness is
 * the point — the list pages are kept alive, so returning to the exact route a
 * user left restores the page the way Back does, layout and scroll included.
 */
export const favoritePath = (favorite: ResolvedFavorite): string | null => {
  const target = VIEW_ROUTE[favorite.savedView.context];

  return target
    ? `/${target.prefix}/${favorite.savedView.scopeKey}/views/${savedViewSlug(favorite.savedView)}`
    : null;
};

/**
 * The caller's favorites — Linear-style sidebar pins, saved views only for now.
 *
 * Every instance reads the same `ListFavorites` cache entry, and each toggle
 * refetches that entry, so the sidebar, the page-header star, the switcher and
 * the manage page all flip together without sharing any state of their own.
 * "Is this view starred?" is answered from this list rather than from a field
 * on `SavedView`: the pages hold their views in plain refs fetched
 * network-only, which a cache write would never reach.
 */
export const useFavorites = () => {
  const route = useRoute();
  const toast = useToast();
  const { isGranted } = useAuth();

  const canList = computed(() => isGranted('tm:core:favorites:list'));
  const canToggle = computed(
    () => isGranted('tm:core:favorites:create') && isGranted('tm:core:favorites:delete'),
  );

  const { result, refetch } = useQuery(ListFavoritesDocument, () => ({
    enabled: canList.value,
    fetchPolicy: 'cache-and-network',
  }));

  const favorites = computed<ResolvedFavorite[]>(() =>
    (result.value?.favorites ?? []).filter(
      (favorite): favorite is ResolvedFavorite =>
        Boolean(favorite.savedView?.scopeKey),
    ));

  /** Whether the list has answered once — before that, "not a favorite" is not yet known. */
  const loaded = computed(() => result.value !== undefined);

  const favoriteByViewId = computed(
    () => new Map(favorites.value.map((favorite) => [favorite.savedViewId, favorite])),
  );

  const isFavorite = (viewId: string): boolean => favoriteByViewId.value.has(viewId);

  /** Whether the favorite's view is the page currently open — the sidebar highlight. */
  const isActive = (favorite: ResolvedFavorite): boolean => {
    const target = VIEW_ROUTE[favorite.savedView.context];

    if (!target || route.name !== target.name) {
      return false;
    }

    const scope = String(route.params[target.scopeParam] ?? '').toLowerCase();
    const slug = String(route.params.slug ?? '');

    // Only the trailing slugId is authoritative — an old link of a renamed
    // view carries a stale name half and still opens this view.
    return scope === favorite.savedView.scopeKey.toLowerCase()
      && slug.endsWith(favorite.savedView.slugId);
  };

  const refetchFavorites = { refetchQueries: [{ query: ListFavoritesDocument }], awaitRefetchQueries: true };

  const { mutate: addFavorite } = useMutation(AddFavoriteDocument, refetchFavorites);
  const { mutate: removeFavorite } = useMutation(RemoveFavoriteDocument, refetchFavorites);

  /** View ids with a toggle in flight — a second click waits for the first. */
  const pending = ref(new Set<string>());

  const toggle = async (view: { id: string }): Promise<void> => {
    if (pending.value.has(view.id)) {
      return;
    }

    pending.value = new Set(pending.value).add(view.id);

    const existing = favoriteByViewId.value.get(view.id);

    try {
      if (existing) {
        await removeFavorite({ variables: { input: { id: existing.id } } });
      } else {
        await addFavorite({ variables: { input: { savedViewId: view.id } } });
      }
    } catch (error) {
      toast.add({
        title: existing ? 'Failed to remove from favorites' : 'Failed to add to favorites',
        description: resolveGraphqlErrorDescription(error),
        color: 'error',
      });
    } finally {
      const next = new Set(pending.value);

      next.delete(view.id);
      pending.value = next;
    }
  };

  return {
    favorites,
    loaded,
    canList,
    canToggle,
    isFavorite,
    isActive,
    isPending: (viewId: string) => pending.value.has(viewId),
    toggle,
    refetch,
  };
};
